// REZISTENȚA LA FOC, CA DICȚIONAR DE PLANȘĂ — clasa, culoarea ei, emblema ușii.
//
// Un singur loc pentru trei consumatori: pereții (umplerea pe stratul ISU),
// emblemele ușilor (cercul de pe gol) și legenda din stânga jos. Culorile sunt
// cele ale legendei ISU de pe planșele de arhitectură de referință (Oana,
// 20.10.2026), eșantionate din planșă, nu alese aici: un arhitect care citește
// desenul nostru lângă al lui trebuie să vadă același portocaliu la EI 120.
//
// CE SE CITEȘTE E TEXTUL DE PE OBIECT («EI 120», «EI 60-C», «REI 180»), scris
// de generator în `props["architecture.*"].rezistenta` (v.
// `canonical.rezistentaLaFoc`): planșa nu decide clase, le desenează.

/** Clasele de rezistență la foc (minute) din legenda de referință, în ordine. */
export const CLASE = [15, 30, 45, 60, 90, 120, 180, 240] as const;
export type Clasa = (typeof CLASE)[number];

/** Culoarea fiecărei clase, ca pe legenda ISU de referință. */
export const CULOARE_CLASA: Readonly<Record<Clasa, string>> = {
  15: "#893c00",
  30: "#247f00",
  45: "#00a7a6",
  60: "#0019e2",
  90: "#1e0880",
  120: "#f07a00",
  180: "#b40000",
  240: "#540026",
};

/** Peretele de protecție al adăpostului ALA (`ala_perete`, beton armat ≥ 40 cm):
 *  n-are o clasă de rezistență la foc cerută, îl definește grosimea; pe planșa
 *  ISU e gri, cu rândul lui în legendă (Oana, 20.10.2026). */
export const GRI_ALA = "#a6a6a6";

/** Cercurile UM / OM / UP ale legendei de referință — felul foii (ușă metalică,
 *  oblon metalic, ușă plină), nu rezistența — sunt toate în maroul clasei 15. */
export const MARO_FOAIE = "#893c00";

export interface Rezistenta {
  /** Criteriile: `R`, `EI`, `REI`, `E`… — ce e înaintea cifrei. */
  criterii: string;
  minute: Clasa;
  /** `-C`: cu dispozitiv de autoînchidere (uși). */
  autoinchidere: boolean;
}

/** «EI 120» / «EI 60-C» / «REI 180» / «EI₂ 90-C» → clasa. `null` pe orice
 *  altceva, inclusiv pe o cifră care nu e în dicționar: o clasă necunoscută
 *  nu primește o culoare «apropiată», rămâne necolorată și se vede. */
export function parseazaRezistenta(text?: string | null): Rezistenta | null {
  if (!text) return null;
  const m = /^\s*([A-Za-z]+)[₁₂]?\s*(\d+)\s*(-\s*[cC])?\s*$/.exec(text);
  if (!m) return null;
  const minute = Number(m[2]) as Clasa;
  if (!(CLASE as readonly number[]).includes(minute)) return null;
  return { criterii: m[1].toUpperCase(), minute, autoinchidere: !!m[3] };
}

/** Culoarea clasei scrise pe obiect, sau `undefined` când obiectul n-are una. */
export function culoareRezistenta(text?: string | null): string | undefined {
  const r = parseazaRezistenta(text);
  return r ? CULOARE_CLASA[r.minute] : undefined;
}

/** Emblema unui gol, ca pe planșa de referință: cercul scris peste ușă. */
export interface Emblema {
  /** `ei`: ușă cu clasă («EI₂» / «60-C»); `um`: ușă metalică etanșă ALA
   *  (UME); `om`: oblon metalic etanș (OME). */
  fel: "ei" | "um" | "om";
  /** Un rând («UM») sau două («EI₂», «60-C»). */
  randuri: readonly string[];
  culoare: string;
}

/** Emblema golului, din ce poartă obiectul: rezistența (ușile căilor de
 *  evacuare, `architecture.evacuare`) sau indicativul ALA (UME1/UME2 → «UM»,
 *  OME1 → «OM»). Ușa simplă a grupului sanitar (UE) și orice gol fără clasă
 *  n-au emblemă. Indicativul cu pragul rămâne scris alături, de altcineva:
 *  emblema spune felul, eticheta spune care anume (Oana, 20.10.2026). */
export function emblemaGolului(o: {
  tip: "Door" | "Window"; rezistenta?: string; ala?: { indicativ?: string };
}): Emblema | null {
  const r = parseazaRezistenta(o.rezistenta);
  if (r && o.tip === "Door") {
    // EI₂: clasa ușilor, cu indicele 2 al criteriului de izolare termică
    // (SR EN 13501-2), cum o scrie legenda de referință.
    const criterii = r.criterii === "EI" ? "EI₂" : r.criterii;
    return { fel: "ei", culoare: CULOARE_CLASA[r.minute],
             randuri: [criterii, `${r.minute}${r.autoinchidere ? "-C" : ""}`] };
  }
  const ind = o.ala?.indicativ ?? "";
  if (/^UME/.test(ind)) return { fel: "um", randuri: ["UM"], culoare: MARO_FOAIE };
  if (/^OME/.test(ind)) return { fel: "om", randuri: ["OM"], culoare: MARO_FOAIE };
  return null;
}
